import React, { useState, useRef, useEffect, useCallback } from "react";
import axios from "axios";
import AttendancePanel from './AttendancePanel';
import { useMediaQuery, Card, IconButton, Typography, Button, TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Grid, Paper, Menu, MenuItem, Box } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CalendarToday from '@mui/icons-material/CalendarToday';
import ArrowDropDownTwoToneIcon from '@mui/icons-material/ArrowDropDownTwoTone';

export default function WorkersAttendance() {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [presentCount, setPresentCount] = useState(0);
  const [absentCount, setAbsentCount] = useState(0);
  const [workers, setWorkers] = useState([]);
  const [totalWorkingHours, setTotalWorkingHours] = useState(0);

  const dateInputRef = useRef(null);
  const isMobile = useMediaQuery("(max-width:768px)");
  const isTab = useMediaQuery("(max-width:1024px)");
  const isMobileOrTab = isMobile || isTab;
  const [selectedDate, setSelectedDate] = useState(() => {
    return localStorage.getItem("selectedDate") || new Date().toISOString().split("T")[0];
  });


  const handleClearAttendance = () => {
    setFilteredRecords([]);
    setWorkers([]); // Clear attendance panel workers
    setPresentCount(0);
    setAbsentCount(0);
    setTotalWorkingHours(0);
  };

  // Fetch attendance from backend
  const fetchAttendance = useCallback(async () => {
    try {
      const response = await axios.get(`https://enzo-matr.onrender.com/attendance?selectedDate=${selectedDate}&selectedMonth=${selectedMonth}`);

      setAttendanceRecords(response.data.attendanceRecords);
      setFilteredRecords(response.data.attendanceRecords);
      setPresentCount(response.data.presentCount);
      setAbsentCount(response.data.absentCount);
      setTotalWorkingHours(response.data.totalWorkingHours);

      // Use workers array from backend (with image and workerNumber)
      setWorkers(response.data.workers || []);
    } catch (error) {
      console.error("❌ Error fetching attendance data:", error);
    }
  }, [selectedDate, selectedMonth]); // Dependencies for useCallback
  useEffect(() => {
    console.log("Updated workers:", workers);
  }, [workers]);

  useEffect(() => {
    fetchAttendance();
  }, [fetchAttendance]); // No warning now

  // Function to filter data based on user input
  const filterTable = useCallback(() => {
    let filteredData = attendanceRecords.filter(record => {
      const recordDate = new Date(record.date);
      return (
        (!searchTerm || record.worker.toLowerCase().includes(searchTerm.toLowerCase())) &&
        (!selectedDate || recordDate.toISOString().split('T')[0] === selectedDate) &&
        (!selectedMonth || recordDate.toLocaleString('default', { month: 'long' }) === selectedMonth)
      );
    });
    setFilteredRecords(filteredData);
  }, [attendanceRecords, searchTerm, selectedDate, selectedMonth]);
  // Apply filter when inputs change
  useEffect(() => {
    filterTable();
  }, [filterTable]);
  // Handle search
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };
  // Handle date change
  const handleDateChange = (event) => {
    const newDate = event.target.value;
    setSelectedDate(newDate);
    localStorage.setItem("selectedDate", newDate); // Store in localStorage
  };
  // Handle month selection
  const handleMonthClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMonthClose = (month) => {
    setAnchorEl(null);
    if (month) {
      setSelectedMonth(month);
      const filteredData = attendanceRecords.filter((record) => {
        const recordMonth = new Date(record.date).toLocaleString("default", { month: "long" });
        return recordMonth === month;
      });
      setFilteredRecords(filteredData);
    }
  };
  const handleDateButtonClick = () => {
    dateInputRef.current.showPicker();
  };
  const handleViewAll = () => {
    setSearchTerm("");
    setSelectedDate("");
    setSelectedMonth("");
    setFilteredRecords(attendanceRecords);
  };

  return (
    <div style={{ background: "#f5f5f5", padding: isMobile ? "0px" : "0px" }}>
      <Typography variant="h4" align="center" gutterBottom style={{
        fontFamily: "caladea", background: "#fdb913", padding: 4,
        textAlign: "center", fontSize: "30px", color: "white", fontWeight: "bold"
      }}>
        WORKERS ATTENDANCE
      </Typography>

      <Paper
        style={{
          padding: "10px",
          width: "100vw",
          height: "100vh",
          minHeight: "100vh",
          position: "fixed",
          marginTop: 0,
          overflow: "auto",
          zIndex: 1,
        }}
      >
        <Grid container spacing={3}>
          <Grid item xs={5}>
            <AttendancePanel
              workers={workers}
              presentCount={presentCount}
              absentCount={absentCount}
              totalWorkingHours={totalWorkingHours}
            />
          </Grid>
          <Grid item xs={12} md={7}>
            <Card sx={{ p: 2 }}>
              <Grid container spacing={1}>

                {/* Search Field */}
                <Grid item xs={4.9}>
                  <TextField
                    variant="outlined"
                    placeholder="Search Worker or Status"
                    size="small"
                    fullWidth
                    value={searchTerm}
                    onChange={handleSearchChange}
                    InputProps={{
                      sx: { borderRadius: "10px", border: "1px solid" }
                    }}
                  />
                </Grid>

                {/* Search Button */}
                <Grid item>
                  <IconButton
                    sx={{ background: "#2f2f2f", color: "white", borderRadius: "10px" }}
                    onClick={filterTable}
                  >
                    <SearchIcon />
                  </IconButton>
                </Grid>

                {/* View All Button */}
                <Grid item xs={2}>
                  <Button
                    variant="contained"
                    sx={{ background: "#2f2f2f", color: "white", borderRadius: "10px", fontSize: "14px", fontWeight: "bold", width: "100%" }}
                    onClick={handleViewAll}
                  >
                    View All
                  </Button>
                </Grid>

                {/* Month Selection */}
                <Grid item xs={2.3}>
                  <Button
                    variant="contained"
                    color="primary"
                    endIcon={<ArrowDropDownTwoToneIcon style={{ fontSize: "24px", color: "black" }} />}
                    sx={{ background: "#FFA500", color: "black", border: "1px solid", borderRadius: "10px", fontFamily: "source serif pro", fontSize: "14px", fontWeight: "bold" }}
                    fullWidth
                    onClick={handleMonthClick}
                  >
                    {selectedMonth || "Month"}
                  </Button>
                  <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => handleMonthClose(null)}>
                    {["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"].map((month, index) => (
                      <MenuItem key={index} onClick={() => handleMonthClose(month)}>
                        {month}
                      </MenuItem>
                    ))}
                  </Menu>
                </Grid>

                {/* Date Selection */}
                <Grid item xs={2}>
                  <Button
                    variant="contained"
                    color="primary"
                    endIcon={<CalendarToday style={{ fontSize: "24px", color: "white" }} />}
                    sx={{ backgroundColor: "#2f2f2f", width: "120px", color: "white", border: "1px solid", borderRadius: "10px", fontFamily: "source serif pro", fontSize: "14px", fontWeight: "bold", }}
                    onClick={handleDateButtonClick}
                  >
                    Date
                    <input
                      type="date"
                      ref={dateInputRef}
                      style={{ display: "flex", width: "0px", height: "0px", backgroundColor: "#2f2f2f" }}
                      onChange={handleDateChange}
                    />
                  </Button>
                </Grid>
              </Grid>

              {/* Attendance Table */}
              {isMobileOrTab ? (
                <Box
                  sx={{
                    mt: 2,
                    height: "54vh",
                    overflowY: "auto",
                    scrollbarWidth: "none",
                    "&::-webkit-scrollbar": { display: "none" },
                  }}
                >
                  <TableContainer component={Paper} sx={{ boxShadow: "none", background: "transparent" }}>
                    <Table>
                      <TableHead
                        sx={{
                          background: "#2f2f2f",
                          position: "sticky",
                          top: 0,
                          zIndex: 2
                        }}
                      >
                        <TableRow>
                          {["Date", "Worker", "Login", "Logout", "Tot Hr", "State"].map((header, i) => (
                            <TableCell key={i} sx={{ color: "white", textAlign: "center", fontWeight: "bold" }}>
                              {header}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {filteredRecords.length > 0 ? (
                          filteredRecords.map((record, index) => (
                            <TableRow key={index}>
                              {["date", "worker", "login", "logout", "total", "state"].map((key, i) => (
                                <TableCell key={i} sx={{ textAlign: "center" }}>
                                  {key === "state" ? (
                                    <Button sx={{ color: "white", backgroundColor: record.state === "PRESENT" ? "green" : "red" }} fullWidth>
                                      {record.state}
                                    </Button>
                                  ) : record[key]}
                                </TableCell>
                              ))}
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={6} align="center" sx={{ color: "red", fontWeight: "bold" }}>No Today records found</TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              ) : (
                <TableContainer
                  sx={{
                    mt: 2,
                    overflowY: "auto",
                    height: "54vh",
                    scrollbarWidth: "none",
                    "&::-webkit-scrollbar": { display: "none" },
                  }}
                >
                  <Table>
                    <TableHead
                      sx={{
                        background: "#2f2f2f",
                        position: "sticky",
                        top: 0,
                        zIndex: 2
                      }}
                    >
                      <TableRow>
                        {["Date", "Worker", "Login", "Logout", "Tot Hr", "State"].map((header, i) => (
                          <TableCell key={i} sx={{ color: "white", textAlign: "center", fontWeight: "bold" }}>
                            {header}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {filteredRecords.length > 0 ? (
                        filteredRecords.map((record, index) => (
                          <TableRow key={index}>
                            {["date", "worker", "login", "logout", "total", "state"].map((key, i) => (
                              <TableCell key={i} sx={{ textAlign: "center" }}>
                                {key === "state" ? (
                                  <Button sx={{ color: "white", backgroundColor: record.state === "PRESENT" ? "green" : "red" }} fullWidth>
                                    {record.state}
                                  </Button>
                                ) : record[key]}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={6} align="center" sx={{ color: "red", fontWeight: "bold" }}>No Today records found</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Card>
            <Button
              variant="contained"
              sx={{ marginTop: "8px", fontFamily: "caladea", backgroundColor: "#FFA500", fontSize: "28px", height: "60px", width: "100%", color: "black", fontWeight: "bold" }}
              onClick={handleClearAttendance}
            >
              CLEAR ATTENDANCE
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </div>
  );
}
